import React, { useEffect, useState } from 'react';

interface TyreInventoryItem {
  id: string;
  brand: string;
  model: string;
  size: string;
  type: string;
  quantity: number;
  image_url?: string;
  width?: string;
  profile?: string;
  rim_size?: string;
  load_index?: string;
  speed_rating?: string;
  features?: string[];
}

interface TyreInventoryCardProps {
  tyre: TyreInventoryItem;
  // Kept optional for backward compatibility with InventorySection.tsx, which
  // still passes these - no longer used since the action buttons are hidden.
  onMessage?: (tyre: TyreInventoryItem) => void;
  onBookFitting?: (tyre: TyreInventoryItem) => void;
  onRequestQuote?: (tyre: TyreInventoryItem) => void;
}

const TyreInventoryCard: React.FC<TyreInventoryCardProps> = ({
  tyre
}) => {
  const [highlighted, setHighlighted] = useState(false);

  useEffect(() => {
    if (window.location.hash === `#tyre-${tyre.id}`) {
      setHighlighted(true);
      setTimeout(() => {
        document.getElementById(`tyre-${tyre.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
      setTimeout(() => setHighlighted(false), 4000);
    }
  }, [tyre.id]);

  const tyreSize = tyre.width && tyre.profile && tyre.rim_size 
    ? `${tyre.width}/${tyre.profile}R${tyre.rim_size}`
    : tyre.size;

  const specs = [
    tyre.load_index && `Load: ${tyre.load_index}`,
    tyre.speed_rating && `Speed: ${tyre.speed_rating}`,
    tyre.type && `Type: ${tyre.type}`
  ].filter(Boolean);

  if (!tyre || tyre.quantity <= 0) return null;

  return (
    <div
      id={`tyre-${tyre.id}`}
      style={{ scrollMarginTop: '80px' }}
      className={`bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border group ${
        highlighted ? 'border-blue-500 ring-2 ring-blue-400 ring-offset-2' : 'border-gray-100'
      }`}
    >
      {/* Stock Badge */}
      <div className="relative">
        <div className="absolute top-3 right-3 z-10">
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            tyre.quantity > 5 ? 'bg-green-500 text-white' : 'bg-yellow-500 text-white'
          }`}>
            {tyre.quantity > 5 ? 'In Stock' : 'Limited Stock'}
          </span>
        </div>
        
        {/* Tyre Image */}
        <div className="h-48 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
          <div className="w-32 h-32 bg-gray-300 rounded-full overflow-hidden relative flex items-center justify-center">
            {tyre.image_url ? (
              <img
                src={tyre.image_url}
                alt={`${tyre.brand} ${tyre.model}`}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="text-gray-500 text-4xl">🛞</div>
            )}
          </div>
        </div>
      </div>

      <div className="p-4">
        {/* Brand and Model */}
        <div className="mb-2">
          <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
            {tyre.brand} {tyre.model}
          </h3>
          <p className="text-sm text-gray-600">{tyreSize}</p>
        </div>

        {/* Specs */}
        {specs.length > 0 && (
          <div className="mb-3">
            <div className="flex flex-wrap gap-1">
              {specs.slice(0, 2).map((spec, index) => (
                <span key={index} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  {spec}
                </span>
              ))}
              {specs.length > 2 && (
                <span className="text-xs text-gray-500">+{specs.length - 2} more</span>
              )}
            </div>
          </div>
        )}

        {/* Features */}
        {tyre.features && tyre.features.length > 0 && (
          <div className="mb-4">
            <div className="flex flex-wrap gap-1">
              {tyre.features.slice(0, 2).map((feature, index) => (
                <span key={index} className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                  {feature}
                </span>
              ))}
              {tyre.features.length > 2 && (
                <span className="text-xs text-gray-500">+{tyre.features.length - 2} more</span>
              )}
            </div>
          </div>
        )}

        {/* Action buttons hidden for now - booking/messaging system is disabled.
            See the business's contact details on the shop page itself. */}

      </div>
    </div>
  );
};

export default TyreInventoryCard;
